import { QBooksDisabledLogo, XeroDisabledLogo } from '@approvalmax/ui';
import { Box, Button, ConditionWrapper, Flex, Text, Tooltip } from '@approvalmax/ui/src/components';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { integrationActions } from 'modules/integration';
import { useDispatch, useSelector } from 'modules/react-redux';
import { ConnectQboMediumDisabled, ConnectQboMediumTransparent } from 'modules/sprites';
import { memo, useCallback, useMemo } from 'react';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { messages } from './DisconnectedAlert.messages';
import { DisconnectedAlertProps } from './DisconnectedAlert.types';

export const DisconnectedAlert = memo<DisconnectedAlertProps>((props) => {
    const { isNoBudgets, inline } = props;

    const company = useCompanyBasedOnUrl();

    const dispatch = useDispatch();

    const isConnectingToIntegration = useSelector((state) =>
        selectors.ui.isOperationInProgress(state, integrationActions.CONNECT_TO_INTEGRATION)
    );

    const integrationName = company.integration?.integrationType
        ? selectors.integration.getIntegrationTypeName(company.integration.integrationType)
        : '';

    const isQBooksIntegration = company.integration?.integrationType === domain.IntegrationType.QBooks;

    const glLogo = useMemo(() => {
        const logoSize = inline ? 40 : 64;

        switch (company.integration?.integrationType) {
            case domain.IntegrationType.Xero:
                return <XeroDisabledLogo size={logoSize} />;

            case domain.IntegrationType.QBooks:
                return <QBooksDisabledLogo size={logoSize} />;

            default:
                return null;
        }
    }, [company.integration?.integrationType, inline]);

    const handleReconnect = useCallback(() => {
        if (!company.integration?.integrationType) {
            return;
        }

        dispatch(
            integrationActions.connectToIntegration({
                integrationType: company.integration?.integrationType,
                companyId: company.id,
                finalRedirectToPage: 'workflow',
                showGlobalProgress: true,
            })
        );
    }, [company.id, company.integration?.integrationType, dispatch]);

    const isReconnectDisabled = company.isReadonly || company.flags.isAuditor;

    const qboConnectButton = isReconnectDisabled ? <ConnectQboMediumDisabled /> : <ConnectQboMediumTransparent />;

    return (
        <Box radius='small' shadow='small' bordered spacing='24' width={inline ? undefined : 480}>
            <Flex
                direction={inline ? 'row' : 'column'}
                alignItems='center'
                justifyContent={inline ? 'space-between' : 'center'}
                spacing='24'
            >
                {inline ? (
                    <Flex alignItems='center' spacing='12' wrap={false}>
                        {glLogo}

                        <Text font='label' color='midnight70' fontSize='large' fontWeight='medium'>
                            {messages.inlineTitle({
                                integrationName,
                            })}
                        </Text>
                    </Flex>
                ) : (
                    <>
                        {glLogo}

                        <Text
                            color='midnight80'
                            font='headline'
                            fontSize='small'
                            fontWeight='medium'
                            textAlign='center'
                        >
                            {isNoBudgets ? messages.noBudgetsTitle({ integrationName }) : messages.title}
                        </Text>

                        <Text color='midnight100' font='body' fontSize='medium' textAlign='center'>
                            {messages.subtitle({ integrationName })}
                        </Text>
                    </>
                )}

                <ConditionWrapper
                    condition={company.isReadonly}
                    wrapperIf={(children) => (
                        <Tooltip activator={children} placement='bottom-end'>
                            {messages.readOnlyMode}
                        </Tooltip>
                    )}
                >
                    <Button
                        onClick={handleReconnect}
                        disabled={isReconnectDisabled}
                        color={isQBooksIntegration ? 'white100' : 'blue80'}
                        progress={isConnectingToIntegration}
                        noPadding={isQBooksIntegration}
                        icon={isQBooksIntegration}
                    >
                        {isQBooksIntegration ? qboConnectButton : messages.reconnect}
                    </Button>
                </ConditionWrapper>
            </Flex>
        </Box>
    );
});

DisconnectedAlert.displayName = 'DisconnectedAlert';
